import type { ObjectPrivilege } from 'cadenza/access-management/model/object-privilege.gen';
import type { DataAcquisitionActionType } from 'cadenza/repository/model/data-acquisition-action-type.gen';
import type { DataSourceName } from 'cadenza/repository/model/data-source-name.gen';
import type { ObjecttypeBoundState } from 'cadenza/repository/model/objecttype-bound-state.gen';
import { isBoundToWorkbookOrTransient } from 'cadenza/repository/model/objecttype-bound-state.gen';
import type { ObjecttypeIdString } from 'cadenza/repository/model/objecttype-id.gen';
import type { ProjectId } from 'cadenza/repository/model/project-id.gen';
import type { ProjectKind } from 'cadenza/repository/model/project-kind.gen';
import type { PublicationState } from 'cadenza/repository/model/publication-state.gen';
import type { RepositoryItemDto } from 'cadenza/repository/model/repository-item-dto';
import type { RepositoryName } from 'cadenza/repository/model/repository-name.gen';
import type { AttributeRelationGroupDto } from 'cadenza/workbook/api/attribute-relation-group-dto';
import type { ObjecttypeAssociationsDto } from 'cadenza/workbook/api/objecttype-associations-dto';
import type { EnrichmentDto } from 'cadenza/workbook/enrichment/model/enrichment-dto';
import type { AttributeId } from 'cadenza/workbook/model/attribute-id.gen';

/** @see net/disy/cadenza/workbook/web/dto/ObjecttypeDto.java */
export interface Objecttype extends RepositoryItemDto<'OBJECTTYPE'> {
  printName: string;
  /** Name of data source this object type belongs to. */
  dataSourceName: DataSourceName;
  /** Name of the repository the data source belongs to */
  dataSourceRepositoryName: RepositoryName;
  attributes: AttributeId[];
  /** privileges of this objecttype for current user */
  currentUserPrivileges: ObjectPrivilege[];
  /** Map of objecttype related properties. */
  properties?: { [x: string]: string | number | boolean | null };
  /**
   * Data acquisition types (ACQUIRE_LAYER_DATA and/or ACQUIRE_TABLE_DATA) that are supported by the objecttype
   * AND allowed by the current user's permissions.
   */
  allowedDataAcquisitionActionTypes?: DataAcquisitionActionType[];
  /**
   * Data acquisition types (ACQUIRE_LAYER_DATA and/or ACQUIRE_TABLE_DATA) that are supported by the objecttype
   */
  allDataAcquisitionActionTypes?: DataAcquisitionActionType[];
  objecttypeAssociations?: ObjecttypeAssociationsDto;
  /** Bound state of this object type */
  boundState: ObjecttypeBoundState;
  enrichments?: EnrichmentDto[];
  visible: boolean;
  isCombinedObjecttype?: boolean;
  isCdsObjecttype?: boolean;
  sourceObjecttypeId?: ObjecttypeIdString;
  isMarkedAsTemplate: boolean;
  attributeRelationGroups: AttributeRelationGroupDto[];
  underlyingBasicMainObjecttypeId: ObjecttypeIdString;
  projectId?: ProjectId;
  projectKind?: ProjectKind;
  projectPrintName?: string;
  publicationState: PublicationState;
}

/**
 * Do you really mean to use this method? It will only say 'true' if the given objecttype
 * is permanently bound to the workbook. Thus, any new objecttype where the objecttype has just been
 * created and the workbook hasn't been saved will be just transiently bound and not included here.
 * @param objecttype The OT to test
 */
export function isObjecttypePermanentAndBound(objecttype: Objecttype) {
  return objecttype.boundState === 'BOUND_TO_WORKBOOK';
}

/**
 * You will need this version if you care about the OT being in the workbook storage, independent
 * of it being already saved and thus becoming "permanently" bound or still being "transient".
 * @param objecttype The OT to test
 */
export function isObjecttypeBound(objecttype: Pick<Objecttype, 'boundState'>) {
  return objecttype.boundState !== 'UNBOUND';
}

export function isObjecttypeBoundToProject(objecttype: Pick<Objecttype, 'boundState'>) {
  return objecttype.boundState === 'BOUND_TO_PROJECT';
}

export function isObjecttypeBoundToWorkbookOrTransient(objecttype: Pick<Objecttype, 'boundState'>) {
  return isBoundToWorkbookOrTransient(objecttype.boundState);
}

/**
 * Do the objecttype bounds indicate that the objecttype has a presence in the management center?
 * Note that permissions etc. could still prevent a user from accessing the objecttype in the MC.
 */
export function isObjecttypeBoundManageable(objecttype: Pick<Objecttype, 'boundState'>) {
  return !isObjecttypeBound(objecttype) || isObjecttypeBoundToProject(objecttype);
}
